import { OperationBase } from '../types/operation.js';


/**
 * Calculates the total number of scraps after all adjustments.
 * It includes both confirmed and potential scraps.
 *
 * @param operation - The operation to calculate scraps for.
 * @returns The total number of scraps.
 */
export function getTotalScraps(operation: OperationBase<any>): number {
    const confirmedScrap = getConfirmedScraps(operation);
    const potentialScrap = getPotentialScraps(operation);
    const scrapCount = confirmedScrap + potentialScrap;
    return scrapCount;
}

/**
 * Calculates the number of confirmed scraps after all adjustments.
 * It sums the initial scrap count and any added confirmed scraps.
 *
 * @param operation - The operation to calculate confirmed scraps for.
 * @returns The number of confirmed scraps.
 */
export function getConfirmedScraps(operation: OperationBase<any>): number {
    const initial = operation.confirmedScraps ?? 0;
    const added = getAddedConfirmedScraps(operation);
    const confirmedScrap = initial + added;
    return confirmedScrap;
}

/**
 * Calculates the number of added confirmed scraps from scrap review and scrap close form.
 *
 * @param operation - The operation to calculate added confirmed scraps for.
 * @returns The number of added confirmed scraps.
 */
export function getAddedConfirmedScraps(operation: OperationBase<any>): number {
    const fromReview = operation.scrapReview?.result === 'scrap'
        ? operation.potentialScraps ?? 0
        : 0;
    const fromClose = operation.scrapCloseForm?.addedConfirmedScrap ?? 0;
    return fromReview + fromClose;
}

/**
 * Calculates the number of potential scraps after all adjustments.
 * If the scrap case has been handled (e.g., closed or reviewed), it returns 0.
 *
 * @param operation - The operation to calculate potential scraps for.
 * @returns The number of potential scraps.
 */
export function getPotentialScraps(operation: OperationBase<any>): number {
    if (!operation.potentialScraps || isScrapCaseHandled(operation)) {
        return 0
    } else {
        return operation.potentialScraps;
    }
}

export function isScrapCaseHandled(operation: OperationBase<any>): boolean {
    return operation.scrapCloseForm != null
        || operation.scrapReview?.result === 'release'
        || operation.scrapReview?.result === 'scrap';
}
